import { useEffect, useState } from 'react';
import { App, Button, Drawer, Form, Input, Space, Typography } from 'antd';
import { useAdminApi } from '../admin-api-context.js';

export type MutationTemplate = {
  label: string;
  method?: 'POST' | 'PATCH';
  path: string;
  body: Record<string, unknown>;
  sensitive?: boolean;
};

export function JsonMutationDrawer({
  template,
  onClose,
  onComplete,
}: {
  template?: MutationTemplate | undefined;
  onClose: () => void;
  onComplete: () => void;
}) {
  const api = useAdminApi();
  const { message } = App.useApp();
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(template ? JSON.stringify(template.body, null, 2) : '');
  }, [template]);

  async function submit(): Promise<void> {
    if (!template) return;
    setSaving(true);
    try {
      const parsed: unknown = JSON.parse(value);
      await api.send(template.method ?? 'POST', template.path, parsed);
      await message.success(`${template.label} saved`);
      setValue('');
      onComplete();
    } catch (error) {
      await message.error(
        error instanceof Error ? error.message : 'The change failed',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      title={template?.label}
      open={template !== undefined}
      width={640}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={() => void submit()}>
            Validate and save
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary">
        This operator payload is validated strictly by the platform. Unknown
        fields are rejected. Secret values are encrypted and never returned.
      </Typography.Paragraph>
      {template?.sensitive ? (
        <Typography.Paragraph type="warning">
          This form contains a secret. Clear clipboard history after pasting it.
        </Typography.Paragraph>
      ) : null}
      <Form layout="vertical">
        <Form.Item label="Request body">
          <Input.TextArea
            value={value}
            onChange={(event) => setValue(event.target.value)}
            autoSize={{ minRows: 14, maxRows: 28 }}
            spellCheck={false}
            className="json-editor"
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
